/**
 * 前后端共用的输入校验（zod 单一事实来源，见方案 4.6）。
 * 后端用于路由层强校验并拒绝未知字段；前端用于表单即时提示。
 */
import { z } from 'zod';
import {
  ACCOUNT_ROLES,
  CHECKIN_STATUSES,
  COMMENT_TARGETS,
  CREATOR_TYPES,
  GENDERS,
  GRADE_CODES,
  MAX_POINTS_PER_UNIT,
  MAX_POINTS_UNIT_SIZE,
  MIN_POINTS_UNIT_SIZE,
  NODE_STATUSES,
  POINTS_REASONS,
  RECURRENCES,
  REDEMPTION_STATUSES,
  SHARE_PERMISSIONS,
  UI_MODES,
  USER_NODE_KINDS,
} from './constants.js';
import { normalizePointsDelta } from './points-sign.js';

// ---------- 基础类型 ----------

/** 用户名：3~32 位，字母开头，允许字母/数字/下划线/连字符（大小写不敏感，库里用 citext）。 */
export const usernameSchema = z
  .string()
  .trim()
  .min(3, '用户名至少 3 位')
  .max(32, '用户名最多 32 位')
  .regex(/^[A-Za-z][A-Za-z0-9_-]*$/, '用户名需以字母开头，只能包含字母、数字、下划线和连字符');

export const emailSchema = z.string().trim().toLowerCase().email('请输入正确的邮箱地址').max(254);

export const nicknameSchema = z.string().trim().min(1, '请填写昵称').max(32, '昵称最多 32 位');

/** 密码只做长度与非空校验，具体强度由 checkPasswordStrength 判断（避免错误信息重复且不一致）。 */
export const passwordSchema = z.string().min(8, '密码太短').max(200, '密码过长');

/** 'YYYY-MM-DD' 本地日期字符串。 */
export const localDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式应为 YYYY-MM-DD')
  .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), '日期不存在');

/**
 * 'HH:mm' 或 'HH:mm:ss' 本地时刻。
 *
 * 接受两种写法是因为来源不同：前端表单写 `'19:00'`，而 PostgreSQL 的 `time` 列
 * 读出来是 `'19:00:00'`（回填 / 复制上次结构时会遇到）。响应里一律规范成 `'HH:mm'`
 * （见 shared 的 `formatTimeOfDay`）。
 */
export const localTimeSchema = z
  .string()
  .trim()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, '时刻格式应为 HH:mm（24 小时制）');

/** 周期长度（年/月/日），用于"实现周期"式输入。 */
export const periodSchema = z.object({
  years: z.number().int().min(0).max(50).default(0),
  months: z.number().int().min(0).max(600).default(0),
  days: z.number().int().min(0).max(3650).default(0),
});

/** 分页参数。 */
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

export const idSchema = z.string().uuid('无效的 ID');

/**
 * 查询串里的布尔值。
 *
 * 为什么不直接用 `z.coerce.boolean()`：`Boolean('false') === true`，
 * 于是 `?activeOnly=false` 会被静默当成 true —— 这类"看起来生效其实相反"的
 * 参数最容易在排查线上问题时浪费时间。这里显式只认四种写法。
 */
export const queryBooleanSchema = z
  .enum(['true', 'false', '1', '0'])
  .transform((v) => v === 'true' || v === '1');

// ---------- 认证 ----------

export const parentRegisterSchema = z.object({
  nickname: nicknameSchema,
  username: usernameSchema,
  email: emailSchema,
  password: passwordSchema,
  /** 身份：爸爸 / 妈妈 / 自定义标签（不参与权限判断，见方案 1.2 ⑩）。 */
  relationLabel: z.string().trim().min(1, '请选择或填写身份').max(16),
  familyName: z.string().trim().min(1, '请填写家庭名称').max(32).optional(),
});

export const emailCodeConfirmSchema = z.object({
  email: emailSchema,
  code: z.string().trim().regex(/^\d{6}$/, '验证码为 6 位数字'),
});

export const loginSchema = z.object({
  /**
   * 登录角色。除家长/学生外还允许 `system_admin`（见方案 2B）：
   * 管理员的角色值存在 auth_user.role 上，登录时必须显式声明角色，
   * 否则同名的家长账号与管理员账号会被混在一起匹配。
   */
  role: z.enum(ACCOUNT_ROLES),
  username: usernameSchema,
  password: z.string().min(1, '请输入密码').max(200),
  /** 图形验证码答案；失败次数达到阈值后后端会强制要求。 */
  captchaAnswer: z.string().trim().max(16).optional(),
  /** 图形验证码会话 ID，由 GET /auth/captcha 返回。 */
  captchaId: z.string().trim().max(64).optional(),
  /**
   * 本设备在该账号上已连续失败的次数，由前端保存并回传。
   * 达到 CAPTCHA_AFTER_FAILURES 后后端强制要求图形验证码（见方案 5.4）。
   */
  captchaFailures: z.number().int().min(0).max(1000).default(0),
  /** 家长开启 TOTP 后必填。 */
  totpCode: z.string().trim().regex(/^\d{6}$/, '动态口令为 6 位数字').optional(),
  /** 勾选"下次不用输密码"：下发长期 Refresh Cookie。 */
  rememberDevice: z.boolean().default(false),
});

export const passwordResetRequestSchema = z.object({ email: emailSchema });

export const passwordResetConfirmSchema = z.object({
  email: emailSchema,
  code: z.string().trim().regex(/^\d{6}$/, '验证码为 6 位数字'),
  newPassword: passwordSchema,
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
});

export const totpVerifySchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/, '动态口令为 6 位数字'),
});

// ---------- 学生 ----------

export const createStudentSchema = z.object({
  nickname: nicknameSchema,
  username: usernameSchema,
  gender: z.enum(GENDERS),
  birthday: localDateSchema,
  gradeCode: z.enum(GRADE_CODES as unknown as [string, ...string[]]),
  /** 可选：家长指定初始密码；不传则由服务端生成强密码并一次性展示。 */
  initialPassword: passwordSchema.optional(),
});

export const updateStudentSchema = z
  .object({
    nickname: nicknameSchema.optional(),
    gender: z.enum(GENDERS).optional(),
    birthday: localDateSchema.optional(),
    gradeCode: z.enum(GRADE_CODES as unknown as [string, ...string[]]).optional(),
    /** 覆盖按年级自动判定的儿童模式（见方案 2.5）。 */
    uiModeOverride: z.enum(UI_MODES).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

export const resetStudentPasswordSchema = z.object({
  newPassword: passwordSchema.optional(),
  /** true 表示强制学生下次登录必须改密（默认 true）。 */
  forceChangeOnNextLogin: z.boolean().default(true),
});

// ---------- 共享 ----------

export const createShareSchema = z
  .object({
    /** 二选一：按用户名或邮箱邀请对方家长。 */
    targetUsername: usernameSchema.optional(),
    targetEmail: emailSchema.optional(),
    permission: z.enum(['editor', 'viewer']).default('editor'),
  })
  .refine((v) => Boolean(v.targetUsername) !== Boolean(v.targetEmail), '请选择用用户名或邮箱其中一种方式邀请');

export const updateShareSchema = z.object({
  permission: z.enum(['editor', 'viewer']),
});

// ---------- 目标 / 任务 ----------

export const createNodeSchema = z
  .object({
    /**
     * ⚠️ 只接受 `goal` / `task`（`USER_NODE_KINDS`）：`kind='homework'` 的节点
     * 只能由作业模块按固定结构创建，通用节点接口放行它只会造出"目标页看不见、
     * 作业页也认不出"的脏数据（见 constants.ts 的说明）。
     */
    kind: z.enum(USER_NODE_KINDS),
    parentId: idSchema.nullable().default(null),
    name: z.string().trim().min(1, '请填写名称').max(64, '名称最多 64 字'),
    note: z.string().trim().max(1000, '备注最多 1000 字').default(''),
    startDate: localDateSchema,
    /** 与 period / isLongTerm 三选一。 */
    endDate: localDateSchema.nullable().default(null),
    period: periodSchema.nullable().default(null),
    isLongTerm: z.boolean().default(false),
  })
  .refine(
    (v) => v.isLongTerm || Boolean(v.endDate) || Boolean(v.period),
    '请选择截止日期、实现周期或"长期"',
  )
  .refine((v) => !(v.endDate && v.period), '截止日期与实现周期只能填一个');

export const updateNodeSchema = z
  .object({
    name: z.string().trim().min(1).max(64).optional(),
    note: z.string().trim().max(1000).optional(),
    startDate: localDateSchema.optional(),
    endDate: localDateSchema.nullable().optional(),
    period: periodSchema.nullable().optional(),
    isLongTerm: z.boolean().optional(),
    status: z.enum(NODE_STATUSES).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

/**
 * **拖放排序（打卡任务级）**：只给一串 habit id，按数组顺序重写 `habit.sort_order`。
 *
 * 为什么是"整串 id"而不是"id + 目标位置"：
 *   · 拖放天然产生的是**完整的新顺序**（前端已经在本地重排过），传回来最直白；
 *   · 服务端只要 `index * 10` 逐个写，不需要"把后面的都挪一下"这类容易写错的位移逻辑；
 *   · 幂等：同一串 id 反复提交，结果完全一致。
 */
export const reorderHabitsSchema = z.object({
  ids: z
    .array(idSchema)
    .min(1, '至少要给一个')
    .max(200, '一次最多排 200 个')
    .refine((list) => new Set(list).size === list.length, '同一个 id 不能出现两次'),
});

// ---------- 打卡任务（habit） ----------

export const createHabitSchema = z
  .object({
    nodeId: idSchema,
    name: z.string().trim().min(1, '请填写名称').max(64),
    recurrence: z.enum(RECURRENCES),
    /** weekly：0=周日 … 6=周六，可多选。 */
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    /** monthly：1..31，另可用 -1 表示"每月最后一天"。 */
    monthDays: z.array(z.number().int().min(-1).max(31)).max(31).default([]),
    /** custom：每 N 天一次。 */
    intervalDays: z.number().int().min(1).max(365).nullable().default(null),
    /** 量化目标（可选）：如 30 分钟 / 20 页。 */
    targetValue: z.number().positive().max(100000).nullable().default(null),
    unit: z.string().trim().max(8).nullable().default(null),
    /**
     * 积分粒度 N：「**每 N 个单位**得 `pointsPerUnit` 分」（见方案 3.4.1）。
     *
     * 用来表达「0.5 分/分钟」这类小数粒度而**不动账本**：
     * 「每 2 分钟得 1 分」≡ 0.5 分/分钟。
     *
     * ⚠️ `min(1)` 必须与 DB 的 `ck_habit_points_unit_size`（`>= 1`）保持一致：
     * `0` 会让算分公式除零。默认 `1` = 改动前的「每单位得 M 分」，存量行为不变。
     * ⚠️ 上限 10000：单位数超过它已经没有实际意义，且能挡住"手滑多按几个 0"
     * 把分值算成 0（N 越大分越小）。
     */
    pointsUnitSize: z.number().int().min(MIN_POINTS_UNIT_SIZE).max(MAX_POINTS_UNIT_SIZE).default(1),
    /** 每 `pointsUnitSize` 个单位得多少积分（见方案 3.4.1）。 */
    pointsPerUnit: z.number().int().min(0).max(MAX_POINTS_PER_UNIT).default(0),
    /** 单日最多计多少分，防止刷分。 */
    dailyPointsCap: z.number().int().min(0).max(10000).nullable().default(null),
    /** 补卡宽限期（小时）。 */
    graceHours: z.number().int().min(0).max(168).default(24),
    activeFrom: localDateSchema,
    activeTo: localDateSchema.nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.recurrence === 'weekly' && v.weekdays.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '请选择每周的哪几天', path: ['weekdays'] });
    }
    if (v.recurrence === 'monthly' && v.monthDays.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '请选择每月的哪几天', path: ['monthDays'] });
    }
    if (v.recurrence === 'custom' && !v.intervalDays) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '请填写间隔天数', path: ['intervalDays'] });
    }
    if (v.targetValue !== null && !v.unit) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '设置目标值时请填写单位', path: ['unit'] });
    }
  });

export const updateHabitSchema = z
  .object({
    name: z.string().trim().min(1).max(64).optional(),
    /**
     * 频率（2026-10-26 补）：
     *
     * 漏了这 4 个字段，导致「编辑打卡任务」里把频率改成「自定义 每 2 天」后
     * **保存不上** —— zod 的 `.object()` 默认丢弃未声明的键，请求带着
     * `recurrence`/`intervalDays` 上来，服务端只写 `name` 等字段，频率永远是旧的。
     *
     * 三个字段都**可选**：只改名字时不需要把频率一起传上来。
     */
    recurrence: z.enum(RECURRENCES).optional(),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    monthDays: z.array(z.number().int().min(-1).max(31)).max(31).optional(),
    intervalDays: z.number().int().min(1).max(365).nullable().optional(),
    /** 「每 N 个单位得 M 分」里的 N；与 `ck_habit_points_unit_size`（>= 1）一致。 */
    pointsUnitSize: z.number().int().min(MIN_POINTS_UNIT_SIZE).max(MAX_POINTS_UNIT_SIZE).optional(),
    pointsPerUnit: z.number().int().min(0).max(MAX_POINTS_PER_UNIT).optional(),
    dailyPointsCap: z.number().int().min(0).max(10000).nullable().optional(),
    graceHours: z.number().int().min(0).max(168).optional(),
    targetValue: z.number().positive().max(100000).nullable().optional(),
    unit: z.string().trim().max(8).nullable().optional(),
    activeTo: localDateSchema.nullable().optional(),
    active: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    // 与 createHabitSchema 同口径：weekly 必须有 weekday、monthly 必须有 monthDay、
    // custom 必须有 intervalDays。只在**本次真的要改成这个 recurrence** 时校验，
    // 避免「只改名字」被历史数据的空 weekdays 挡住。
    if (v.recurrence === 'weekly' && (v.weekdays?.length ?? 0) === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '请选择每周的哪几天', path: ['weekdays'] });
    }
    if (v.recurrence === 'monthly' && (v.monthDays?.length ?? 0) === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '请选择每月的哪几天', path: ['monthDays'] });
    }
    if (v.recurrence === 'custom' && !v.intervalDays) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '请填写间隔天数', path: ['intervalDays'] });
    }
  })
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

// ---------- 打卡与计时 ----------

export const startCheckinSchema = z.object({
  /** 开始计时前必须设置预计完成时长（见方案 2.3）。 */
  estimatedSeconds: z
    .number()
    .int()
    .min(60, '预计时长至少 1 分钟')
    .max(12 * 3600, '预计时长最多 12 小时'),
});

export const finishCheckinSchema = z.object({
  /** 量化目标完成值；未填则按 1 个"次"计。 */
  valueDone: z.number().positive().max(100000).nullable().default(null),
  note: z.string().trim().max(500).default(''),
});

export const skipCheckinSchema = z.object({
  reason: z.string().trim().max(200).default(''),
});

/**
 * 「撤销放弃」（`POST /checkins/:id/restore`）：把一条 `skipped` 退回 `pending`。
 *
 * `reason` 与 `skipCheckinSchema` 同样是**可选**的（上限 200）：
 * 恢复通常是"点错了，赶紧退回来"，强制填理由只会让孩子放弃这个入口。
 * 但它一旦填了，会和"谁 / 什么时候 / 从什么状态恢复成什么"一起写进
 * `checkin_event.meta`（`event='undo'`，见 `checkins.service.ts` 的 `restoreSkippedCheckin`）——
 * 留痕不依赖它，它只是给人看的一句话。
 *
 * ⚠️ 刻意**不**复用 `skipCheckinSchema`：两者眼下字段相同，但语义相反。
 * 将来若给放弃加字段（例如"放弃分类"），不该顺带出现在恢复的入参里。
 */
export const restoreCheckinSchema = z.object({
  reason: z.string().trim().max(200).default(''),
});

/**
 * 家长打回作业（`POST /checkins/:id/reject`）。
 *
 * `reason` 是**可选**的：家长在手机上常只点一下"打回重做"，
 * 强制填写理由会让这个动作变成负担，于是验收流程被跳过 —— 那才是真正的损失。
 * 但一旦填了，它会被写进 `checkin_event.meta` 与 `checkin.note`（孩子能看到要改什么）。
 */
export const rejectCheckinSchema = z.object({
  reason: z.string().trim().max(500).default(''),
});

export const listCheckinsSchema = paginationSchema.extend({
  from: localDateSchema.optional(),
  to: localDateSchema.optional(),
  status: z.enum(CHECKIN_STATUSES).optional(),
});

/**
 * 今日待打卡查询（`GET /student/today`）。
 * 学生本人可以省略 `studentId`（默认自己）；家长必须显式指定要看哪个孩子。
 */
export const studentTodayQuerySchema = z.object({
  studentId: idSchema.optional(),
});

/**
 * 打卡日历查询（`GET /checkins`）。
 * 过滤与分页沿用 `listCheckinsSchema`，只额外允许省略 `studentId`（学生端默认自己）。
 */
export const checkinsQuerySchema = listCheckinsSchema.extend({
  studentId: idSchema.optional(),
});

// ---------- 学校作业（见 apps/api/src/modules/homework/） ----------
//
// 结构（写进这里的目的是让前后端对"一天一份作业单"的形状有同一份契约）：
//   作业单 = node(kind='homework', parent_id=NULL, depth=1, start_date=end_date=该日期)
//   科目   = node(kind='homework', parent_id=作业单, depth=2)  ← 叶子，挂打卡任务
//   作业项 = habit(recurrence='once', need_approval=true, active_from=active_to=该日期)
//
// 于是"服务端计时、家长验收、积分、超时、叶子约束"全部复用既有机制。

/**
 * 一条作业项（作业明细）。
 *
 * `pointsPerUnit` 是**验收通过时**才入账的分值（`need_approval = true`，
 * 孩子点完成只会到 `submitted`）。
 *
 * ⚠️ 它是 `optional` 而**不是** `.default(0)`：`0` 的语义是"这一项明确不计分"，
 * 而"请求里没有这个字段"的语义是"没设置"—— 两者在**入参边界上**必须能区分开。
 * 没设置时由服务端落到 `HOMEWORK_DEFAULT_POINTS_PER_UNIT`（**当前 0 分**：
 * 本项目的既定语义就是作业项不计分，所以两条路径当前落出同一个数），
 * 判定见 `resolveHomeworkItemPoints()`。
 *
 * 量化目标（`targetValue` + `unit`）对作业一般用不上，但沿用 habit 的既有字段，留空即可。
 */
export const homeworkItemInputSchema = z
  .object({
    name: z.string().trim().min(1, '请填写作业项名称').max(64, '作业项名称最多 64 字'),
    pointsPerUnit: z.number().int().min(0).max(1000).optional(),
    targetValue: z.number().positive().max(100000).nullable().default(null),
    unit: z.string().trim().max(8).nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.targetValue !== null && !v.unit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '设置目标值时请填写单位',
        path: ['unit'],
      });
    }
  });

/**
 * 一个科目。
 *
 * 时刻三兄弟的输入规则（服务端的派生见 `homework.rules.ts` 的 `resolveSubjectSchedule`）：
 *  - `dueTime` 与 `durationMinutes` **只能填一个**（填了时长就是在说"从开始时刻起算 N 分钟"）；
 *  - 填 `durationMinutes` 必须同时有 `startTime`，否则没法算出截止时刻；
 *  - 三个都留空 → 服务端按"当天完成"落成默认截止 `23:59`
 *    （`HOMEWORK_DEFAULT_DUE_TIME`，响应里 `dueTimeSource: 'dayEnd'`）——
 *    绝不留下 NULL 的 `due_time`，那会让 `due_at` 退化成当天 0 点、从 00:00 就判超时。
 *
 * **截止日期**（`dueDate`，2026-10-16 新增的第二个维度）：
 *  - `dueDate` = **哪一天截止**（存在该科目的**独立一列** `node.due_date`，迁移 0012；
 *    见 `resolveSubjectDueDate`）；上面那三个字段只管**那一天几点**。
 *    周五布置、周日截止的作业因此变得可以表达：
 *    `dueDate: '2026-10-18'` + 默认的 `23:59` → `checkin.due_at` = 周日 23:59。
 *    ⚠️ 它**不在** `node.end_date` 上：那是节点树的计划区间，触发器
 *    `guard_node_tree()` 会拒绝"子级区间超出上级"（用户实测报过
 *    `NODE_DATE_OUT_OF_PARENT_RANGE`）。
 *  - `null` / 省略 = **与布置日同一天**（= 改造前的行为，也是一条向后兼容的出口：
 *    老客户端不传这个字段时行为逐字不变）。
 *  - 默认策略（周五/周六布置 → 周日）**由客户端算好再显式传上来**
 *    （`@fht/shared` 的 `defaultHomeworkDueDate()`）：这样"家长看到的那一天"
 *    与"存下去的那一天"永远是同一个值，不存在两处各算一遍的漂移。
 */
export const homeworkSubjectInputSchema = z
  .object({
    name: z.string().trim().min(1, '请填写科目名称').max(64, '科目名称最多 64 字'),
    startTime: localTimeSchema.nullable().default(null),
    dueTime: localTimeSchema.nullable().default(null),
    durationMinutes: z.number().int().min(1).max(1440).nullable().default(null),
    /** 截止日期（'YYYY-MM-DD'）；`null` = 与布置日同一天。范围校验在服务层。 */
    dueDate: localDateSchema.nullable().default(null),
    /** 科目下的作业项：**至少 1 项**（空科目无法表达"这科完成了"，只会让完成判定失去意义）。 */
    items: z
      .array(homeworkItemInputSchema)
      .min(1, '每个科目至少要有 1 项作业')
      .max(50, '单个科目最多 50 项作业'),
  })
  .superRefine((v, ctx) => {
    if (v.dueTime && v.durationMinutes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '截止时刻与时长只能填一个',
        path: ['durationMinutes'],
      });
    }
    if (v.durationMinutes && !v.startTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '填写时长时必须同时填写开始时刻',
        path: ['startTime'],
      });
    }
  });

/**
 * 整单保存（`PUT /students/:id/homework?date=`）的请求体。
 *
 * `subjects: []` 是合法的"这天没有作业"：会把当天的作业单清空
 * （仍然遵守"有真实打卡记录的作业项只停用、不删除"的规则；若清空后不剩任何科目，
 * 连作业单节点本身也一并删除，于是 `GET` 会返回 `exists: false`）。
 */
export const saveHomeworkSchema = z
  .object({
    /** 作业单名称；省略时服务端用「<日期> 的作业」。 */
    name: z.string().trim().min(1, '请填写作业单名称').max(64).optional(),
    subjects: z.array(homeworkSubjectInputSchema).max(20, '一天最多 20 个科目').default([]),
  })
  .superRefine((v, ctx) => {
    // 科目按**名称**匹配已有节点（同名即同一科，保留其下作业项的打卡记录），
    // 因此重名必须在这里就拒绝，否则"覆盖"会变成"到底改的是哪一科"的随机行为。
    const seen = new Set<string>();
    for (const [index, subject] of v.subjects.entries()) {
      const key = subject.name.trim().toLocaleLowerCase();
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `科目「${subject.name}」重复了`,
          path: ['subjects', index, 'name'],
        });
      }
      seen.add(key);
    }
  });

/**
 * 「复制上次的结构」（`POST /students/:id/homework/copy`）。
 *
 * `fromDate` 省略时服务端自动挑选"目标日期之前、最近的一份**有科目的**作业单"。
 */
export const copyHomeworkSchema = z.object({
  date: localDateSchema,
  fromDate: localDateSchema.optional(),
});

/** 取某天的作业单（`GET`/`PUT /students/:id/homework`）。省略 `date` = 家庭时区下的今天。 */
export const homeworkDateQuerySchema = z.object({
  date: localDateSchema.optional(),
});

/** 过往作业（`GET /students/:id/homework/history`）：区间缺省为最近 30 天。 */
export const homeworkHistoryQuerySchema = z.object({
  from: localDateSchema.optional(),
  to: localDateSchema.optional(),
});

/** 「超时」队列（`GET /students/:id/homework/overdue`）。 */
export const homeworkOverdueQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

/**
 * 「**还有效**（还没到期、但已经不是今天布置的）」队列
 * （`GET /students/:id/homework/open`）。
 *
 * 入参与「超时」**逐字相同**（同一条 `listCheckinQueue()` 的两个互补查询，
 * 分页/排序口径一致）；命名分开只是为了两边的校验错误信息能指回各自的路由。
 */
export const homeworkOpenQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

// ---------- 积分 / 奖品 / 兑换 ----------

/**
 * 家长手动奖励 / 扣分（`POST /students/:id/points/adjust`）。
 *
 * ## 符号由「类型」决定，不由家长手打（2026-10-18 生产 bug 的修复点）
 *
 * 旧实现把 `delta` 原样入账：家长点「扣分」、把预填的 `-5` 改成 `10`，
 * 库里就出现 `delta=+10, reason='penalty'` —— 一条**顶着"扣分"名字的加分**。
 * 流水表只追加不可改，这种脏数据只能靠冲正，代价很实在。
 *
 * 所以这里在**校验层就归一化符号**（`normalizePointsDelta`）：
 *   - `penalty` → 恒为负（填 `10` 或 `-10` 都得到 `-10`）；
 *   - `bonus`   → 恒为正；
 *   - `adjust`  → 原样（这个类型的语义就是"方向我来定"）。
 *
 * 归一化放在 schema（请求边界）里，`points.service.adjustPoints()` 再兜一次，
 * `applyPointsEntry()` 在写库前做最后一道断言 —— 三层都引同一份
 * `points-sign.ts`，不会各说各话。
 */
export const adjustPointsSchema = z
  .object({
    delta: z
      .number()
      .int()
      .refine((v) => v !== 0, '积分变动不能为 0')
      .refine((v) => Math.abs(v) <= 100000, '单次调整幅度过大'),
    reason: z.enum(['bonus', 'penalty', 'adjust']).default('adjust'),
    note: z.string().trim().min(1, '请填写变动原因').max(200),
  })
  .transform((value) => ({ ...value, delta: normalizePointsDelta(value.reason, value.delta) }));

export const pointsLedgerQuerySchema = paginationSchema.extend({
  from: localDateSchema.optional(),
  to: localDateSchema.optional(),
  reason: z.enum(POINTS_REASONS).optional(),
});

/**
 * 新建奖品。
 *
 * `studentId` 描述**可见范围**（见 packages/db/src/schema.ts 的 reward 注释）：
 *   - 省略或 `null`（默认）→ **共用**：归属家长名下的所有孩子都能看到、都能兑换；
 *   - 给某个孩子的 id → **专属**：只有这个孩子能看到。
 *
 * 它放在 schema 里而不是由路由另外解析，是为了让"创建"与"修改可见范围"
 * （`updateRewardSchema` 由本 schema `.partial()` 得来）用**同一个字段定义**。
 */
export const createRewardSchema = z.object({
  name: z.string().trim().min(1, '请填写奖品名称').max(64),
  description: z.string().trim().max(500).default(''),
  costPoints: z.number().int().min(1, '所需积分至少为 1').max(1000000),
  /** null 表示不限量。 */
  stock: z.number().int().min(0).max(100000).nullable().default(null),
  /** 低龄建议开启：需要家长批准才扣分。 */
  needApproval: z.boolean().default(true),
  imageUrl: z.string().trim().url('请填写正确的图片链接').max(500).nullable().default(null),
  active: z.boolean().default(true),
  /** 可见范围：省略/null = 所有孩子共用；给 id = 仅该孩子可见。 */
  studentId: idSchema.nullable().optional(),
});

export const updateRewardSchema = createRewardSchema.partial().refine(
  (v) => Object.keys(v).length > 0,
  '没有需要更新的内容',
);

export const requestRedemptionSchema = z.object({
  rewardId: idSchema,
  note: z.string().trim().max(200).default(''),
});

export const decideRedemptionSchema = z.object({
  note: z.string().trim().max(200).default(''),
});

/**
 * 纠错冲正（`POST /points/ledger/:id/reverse`）。
 *
 * 只接受"原因"：金额由被冲正的那笔流水取反得出，**不接受客户端传金额** ——
 * 否则冲正入口就会变成第二个"随手改积分"的通道，账本再也自证不了。
 */
export const reverseLedgerSchema = z.object({
  note: z.string().trim().min(1, '请填写冲正原因').max(200),
});

/** 奖品目录查询（`GET /students/:id/rewards`、`GET /rewards`）。 */
export const listRewardsQuerySchema = z.object({
  /** 只看上架奖品；学生端固定只看上架（不提供关闭开关）。 */
  activeOnly: queryBooleanSchema.optional(),
});

/**
 * 家长维护奖品时的可见范围（奖品**不必**挂在某个孩子名下，见 schema.ts 的 reward 注释）。
 * 省略或 `null` = 共用（归属家长名下所有孩子）；给 id = 专属给那一个孩子。
 */
export const rewardTargetStudentSchema = z.object({ studentId: idSchema.nullable().optional() });

/** 兑换记录查询（`GET /students/:id/redemptions`）。 */
export const listRedemptionsQuerySchema = paginationSchema.extend({
  status: z.enum(REDEMPTION_STATUSES).optional(),
});

// ---------- 点评 ----------

export const createCommentSchema = z.object({
  targetType: z.enum(COMMENT_TARGETS),
  targetId: idSchema,
  content: z.string().trim().min(1, '请填写点评内容').max(1000),
  /** 低龄模式用图形化情绪标记替代长文本。 */
  mood: z.enum(['great', 'good', 'keep_going']).nullable().default(null),
});

/**
 * 新增点评（`POST /comments`）。
 *
 * `studentId` 只在 `targetType='period'` 时需要：周期点评没有对应的数据行，
 * 无法从 target 反查归属学生，必须由调用方显式给出（其余类型传了也会被忽略并核对）。
 */
export const createCommentRequestSchema = createCommentSchema.extend({
  studentId: idSchema.optional(),
});

/** 点评列表查询（`GET /comments?targetType&targetId`）。`studentId` 同上，仅周期点评需要。 */
export const listCommentsQuerySchema = paginationSchema.extend({
  targetType: z.enum(COMMENT_TARGETS),
  targetId: idSchema,
  studentId: idSchema.optional(),
});

// ---------- 统计 ----------

export const statsRangeSchema = z.object({
  from: localDateSchema.optional(),
  to: localDateSchema.optional(),
});

export const timeseriesQuerySchema = statsRangeSchema.extend({
  metric: z.enum(['completion', 'duration', 'points']).default('completion'),
  interval: z.enum(['day', 'week', 'month']).default('day'),
});

// ---------- 系统管理员（见方案 2B） ----------
//
// 注意这里的表单一律**只涉及账号与部署实例**：没有任何一个 schema 接受
// 学生、打卡、积分相关的字段。管理接口能写什么，在这一层就被限定死了。

/** 账号列表查询：分页 + 关键字 + 角色 + 状态过滤（见方案 2B.3 A 类「账号管理」）。 */
export const adminUserQuerySchema = paginationSchema.extend({
  /** 关键字：用户名 / 邮箱 / 昵称，任一命中。 */
  q: z.string().trim().max(64).optional(),
  role: z.enum(ACCOUNT_ROLES).optional(),
  status: z.enum(['active', 'disabled']).optional(),
});

/** 停用 / 启用账号。停用 = 设 deleted_at（软删，可恢复）。 */
export const adminUserStatusSchema = z.object({
  disabled: z.boolean(),
});

/**
 * 修改家庭级设置。
 * **刻意只允许这四个字段** —— 密钥、备份路径、邮件凭据等都不在这里暴露（见方案 2B.3 D 类）。
 */
export const adminSettingsUpdateSchema = z
  .object({
    familyName: z.string().trim().min(1, '请填写家庭名称').max(32),
    timezone: z.string().trim().min(1).max(64),
    registrationOpen: z.boolean(),
    requireInviteCode: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

/** 生成家长注册邀请码（复用 CLI invite-code 的同一段逻辑）。 */
export const adminInviteCodeSchema = z.object({
  /** 有效期（分钟）：5 分钟 ~ 30 天。 */
  ttlMinutes: z.number().int().min(5).max(43_200).default(1440),
});

/** 审计日志查询：分页 + 按操作者 / 动作 / 时间过滤。 */
export const adminAuditQuerySchema = paginationSchema.extend({
  action: z.string().trim().max(64).optional(),
  actorId: z.string().uuid('无效的 ID').optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

/** 邮件自检：向指定邮箱发一封测试邮件。 */
export const adminMailTestSchema = z.object({
  email: emailSchema,
  /** 可选备注，会写进审计日志（便于区分"谁在什么时候验证过邮件配置"）。 */
  note: z.string().trim().max(200).default(''),
});

// ---------- 类型导出（供前端复用） ----------

export type ParentRegisterInput = z.infer<typeof parentRegisterSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type CreateStudentInput = z.infer<typeof createStudentSchema>;
export type UpdateStudentInput = z.infer<typeof updateStudentSchema>;
export type CreateShareInput = z.infer<typeof createShareSchema>;
export type CreateNodeInput = z.infer<typeof createNodeSchema>;
export type UpdateNodeInput = z.infer<typeof updateNodeSchema>;
export type CreateHabitInput = z.infer<typeof createHabitSchema>;
export type UpdateHabitInput = z.infer<typeof updateHabitSchema>;
export type StartCheckinInput = z.infer<typeof startCheckinSchema>;
export type FinishCheckinInput = z.infer<typeof finishCheckinSchema>;
export type RejectCheckinInput = z.infer<typeof rejectCheckinSchema>;
export type SkipCheckinInput = z.infer<typeof skipCheckinSchema>;
export type RestoreCheckinInput = z.infer<typeof restoreCheckinSchema>;
export type StudentTodayQueryInput = z.infer<typeof studentTodayQuerySchema>;
export type CheckinsQueryInput = z.infer<typeof checkinsQuerySchema>;
export type HomeworkItemInput = z.infer<typeof homeworkItemInputSchema>;
export type HomeworkSubjectInput = z.infer<typeof homeworkSubjectInputSchema>;
export type SaveHomeworkInput = z.infer<typeof saveHomeworkSchema>;
export type CopyHomeworkInput = z.infer<typeof copyHomeworkSchema>;
export type HomeworkHistoryQueryInput = z.infer<typeof homeworkHistoryQuerySchema>;
export type HomeworkOverdueQueryInput = z.infer<typeof homeworkOverdueQuerySchema>;
export type HomeworkOpenQueryInput = z.infer<typeof homeworkOpenQuerySchema>;
export type AdjustPointsInput = z.infer<typeof adjustPointsSchema>;
export type ReverseLedgerInput = z.infer<typeof reverseLedgerSchema>;
export type CreateRewardInput = z.infer<typeof createRewardSchema>;
export type UpdateRewardInput = z.infer<typeof updateRewardSchema>;
export type ListRewardsQueryInput = z.infer<typeof listRewardsQuerySchema>;
export type RequestRedemptionInput = z.infer<typeof requestRedemptionSchema>;
export type ListRedemptionsQueryInput = z.infer<typeof listRedemptionsQuerySchema>;
export type CreateCommentInput = z.infer<typeof createCommentSchema>;
export type CreateCommentRequestInput = z.infer<typeof createCommentRequestSchema>;
export type ListCommentsQueryInput = z.infer<typeof listCommentsQuerySchema>;
export type PaginationInput = z.infer<typeof paginationSchema>;
export type CreatorTypeValue = (typeof CREATOR_TYPES)[number];

// 系统管理员（见方案 2B）
export type AdminUserQueryInput = z.infer<typeof adminUserQuerySchema>;
export type AdminUserStatusInput = z.infer<typeof adminUserStatusSchema>;
export type AdminSettingsUpdateInput = z.infer<typeof adminSettingsUpdateSchema>;
export type AdminInviteCodeInput = z.infer<typeof adminInviteCodeSchema>;
export type AdminAuditQueryInput = z.infer<typeof adminAuditQuerySchema>;
export type AdminMailTestInput = z.infer<typeof adminMailTestSchema>;
